import { api } from '@/config/api';

export type CrmProvider = 'hubspot' | 'salesforce' | 'pipedrive' | 'zoho';
export type SyncTrigger = 'on_qualified' | 'on_won' | 'manual' | 'realtime';
export type SyncStatus = 'idle' | 'syncing' | 'error';

export interface CrmConnection {
  connectionUid: string;
  companyUid: string;
  provider: CrmProvider;
  isActive: boolean;
  syncStatus: SyncStatus;
  syncTrigger: SyncTrigger;
  fieldMappings: Record<string, string>;
  instanceUrl?: string;
  portalId?: string;
  lastSyncAt?: string;
  lastSyncError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CrmSyncEvent {
  eventUid: string;
  companyUid: string;
  connectionUid: string;
  leadUid: string;
  action: 'create' | 'update' | 'delete' | 'noop';
  status: 'pending' | 'succeeded' | 'failed' | 'skipped';
  externalId?: string;
  error?: string;
  payload?: Record<string, unknown>;
  createdAt: string;
}

export interface ManualTokens {
  access: string;
  refresh?: string;
  instanceUrl?: string;
  portalId?: string;
}

const BASE = '/slighter_crm/crm-sync';

export const crmSyncService = {
  /** List all active CRM connections. */
  listConnections: async (): Promise<CrmConnection[]> =>
    (await api.get(`${BASE}/connections`)).data,

  /** Create a new CRM connection using manual tokens. */
  createConnection: async (provider: CrmProvider, manualTokens: ManualTokens): Promise<CrmConnection> =>
    (await api.post(`${BASE}/connections`, { provider, manualTokens })).data,

  /** Update field mappings or sync trigger. */
  updateConnection: async (
    connectionUid: string,
    patch: { fieldMappings?: Record<string, string>; syncTrigger?: SyncTrigger },
  ): Promise<CrmConnection> =>
    (await api.patch(`${BASE}/connections/${connectionUid}`, patch)).data,

  /** Disconnect (soft-delete) a CRM connection. */
  removeConnection: async (connectionUid: string): Promise<void> => {
    await api.delete(`${BASE}/connections/${connectionUid}`);
  },

  /** Manually push a lead to the CRM. */
  pushLead: async (connectionUid: string, leadUid: string): Promise<CrmSyncEvent> =>
    (await api.post(`${BASE}/connections/${connectionUid}/push-lead/${leadUid}`)).data,

  /** Fetch recent sync events for a connection. */
  getEvents: async (connectionUid: string, limit = 50): Promise<CrmSyncEvent[]> =>
    (await api.get(`${BASE}/connections/${connectionUid}/events`, { params: { limit } })).data,
};
